<template>
  <div class="page-wrapper">
    <loading v-model:active="isLoading" :is-full-page="true" />

    <vue-final-modal v-model="showModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <!-- SurveySummary component displays the report details -->
        <SurveySummary :assessmentDate="assessmentDate" :question-list="questions" />
      </div>
    </vue-final-modal>

    <div class="content-wrapper">
      <div class="header-container">
        <img class="uni-logo" :src="`${publicPath}assets/logo.png`" alt="University Logo" />
        <div class="title-container">
          <h1>{{ pageTitle }}</h1>
          <h3>{{ activeSystem == 'rcat' ? 'V: 1.1' : 'V: 4.0' }}</h3>
        </div>
      </div>

      <!-- Search button that toggles search panel -->
      <div class="search-container" v-if="showBegin">
        <button class="search-toggle-btn" @click="toggleSearchPanel">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="currentColor"
              d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <span>{{ isSearchOpen ? 'Hide Search' : 'Search for Case Assessment' }}</span>
        </button>

        <!-- Collapsible search panel -->
        <transition name="slide">
          <div class="reference-search-panel" v-if="isSearchOpen">
            <div class="reference-search">
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#6B7280"
                    d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <input id="referenceSearch" type="text" v-model="referenceNumber" placeholder="Enter reference number"
                  class="form-input" />
              </div>
              <button class="btn-primary search-btn" @click="searchReportByReference">
                Search
              </button>
            </div>
          </div>
        </transition>
      </div>

      <div v-if="showBegin" class="content-block">
        <div class="info-card">
          <p v-if="isLoggedIn() == false">
            Create an account if you wish to save your complexity assessments to review again.
            Registering as a user would also contribute to our ongoing research in the field of endodontics,
            as well as our efforts to improve access to endodontic services in the future.
          </p>

          <div class="action-buttons" v-if="isLoggedIn() == false">
            <div class="btn-group">
              <button class="btn-secondary" v-bind:class="{ 'btn-accent': resume }" v-on:click="login()">
                <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                Login
              </button>
              <button class="btn-secondary" v-bind:class="{ 'btn-accent': resume }" v-on:click="newAssessment()">
                <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                    d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                Register
              </button>
            </div>

            <button class="btn-primary" v-if="!resume" v-on:click="continueAsGuest()">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              Continue As Guest
            </button>
            <button class="btn-primary" v-if="resume" v-on:click="continueAsGuest()">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              Start New Assessment
            </button>
            <button class="btn-secondary" v-if="resume" v-on:click="resumeAssessment()">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor"
                  d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
              Resume Assessment
            </button>
          </div>

          <div class="action-buttons" v-if="isLoggedIn() == true">
            <button class="btn-primary" v-bind:class="{ 'btn-accent': resume }" v-on:click="newAssessment()">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              Start New Assessment
            </button>
            <button class="btn-secondary" v-if="resume" v-on:click="resumeAssessment()">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor"
                  d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
              Resume Assessment
            </button>
          </div>
        </div>
      </div>

      <router-view></router-view>


      <div class="external-links-block" v-if="showBegin">
        <div class="links-container">
          <div class="buttons-container"  v-if="activeSystem == 'ecat'">
            <a href="https://learning.e-cat.uk/" target="_blank" class="link-button">
              <svg class="button-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor"
                  d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 14.54L3 12.25v5.42L12 24l9-6.33v-5.42L12 17.54z" />
              </svg>
              E-Learning Course
            </a>

            <button class="link-button" v-on:click="viewTutorial()">
              <svg class="button-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M8 5v14l11-7z" />
              </svg>
              E-CAT Demo
            </button>
          </div>

          <a href="https://www.e-s-e.eu/" target="_blank" class="ese-logo">
            <p>Partially funded by</p>
            <img :src="`${publicPath}assets/ESE.png`" alt="ESE Logo" />
          </a>

          <div v-if="activeSystem == 'rcat'">
            <p>
              R-CAT is a digital restorative complexity assessment tool, inspired and based on the Endodontic Complexity Assessment Tool (E-CAT) (Essam et al. 2021) and Dental Practicality Index (DPI) (Dawood & Patel 2017).
              The development was funded by research grants awarded by the University of Liverpool and European Society of Endodontics.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SurveySummary from '@/components/SurveySummary.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { VueFinalModal } from 'vue-final-modal';
import moment from 'moment';
import authStore from "../store/auth-store";
import { resetAssessmentStorage } from '@/utils/resetAssessmentStorage';

export default {
  data() {
    return {
      // Modal and report data
      showModal: false,
      assessmentDate: "",
      questions: [],
      referenceNumber: "",
      // Loading state
      isLoading: false,
      // For begin section logic
      showBegin: true,
      resume: false,
      currentUser: null,
      // Toggle for search panel
      isSearchOpen: false,
    };
  },
  created() {
  this.activeSystem = localStorage.getItem('@app-system');
    if (!this.activeSystem) {
      this.activeSystem = "ecat";
    }
  },
  computed: {
    pageTitle() {
      if (this.activeSystem === 'rcat') {
        return 'Restorative Complexity Assessment Tool';
      }
      return 'Endodontic Complexity Assessment Tool';
    },
    user() {
      return authStore.state.user;
    },
    
  },
  mounted() {
    this.loadButton();
    this.updateBeginButton(this.$router.currentRoute.value.path);
  },
  methods: {
    
    // Toggle search panel visibility
    toggleSearchPanel() {
      this.isSearchOpen = !this.isSearchOpen;
      // Clear the reference number when closing
      if (!this.isSearchOpen) {
        this.referenceNumber = "";
      }
    },
    loadButton() {
      const storedQuestionsJson = localStorage.getItem("@screening-questions");
      if (storedQuestionsJson && JSON.parse(storedQuestionsJson).length > 0) {
        this.resume = true;
      } else {
        this.resume = false;
      }
    },
    updateBeginButton(path) {
      this.showBegin = (path === "/");
    },
    continueAsGuest() {
      resetAssessmentStorage({ keepSystem: true });
      this.$router.push("/screening");
    },
    resumeAssessment() {
      this.$router.push("/screening");
    },
    login() {
      this.$router.push("/login");
    },
    newAssessment() {
      resetAssessmentStorage({ keepSystem: true });
      if (this.isLoggedIn()) {
        this.$router.push('/screening');
      } else {
        this.$router.push('/demographic');
      }
    },
    isLoggedIn() {
      return this.user != null;
    },
    viewTutorial() {
      window.open(this.publicPath + "assets/ecatvideo.mp4");
    },
    // New method to search report by reference
    searchReportByReference() {
      if (!this.referenceNumber) {
        alert("Please enter a reference number");
        return;
      }
      this.isLoading = true;
      this.axios.get("/api/report/getbyreference", {
        params: { reference: this.referenceNumber },
        headers: {
          'Authorization': 'Bearer ' + (this.user ? this.user.token : "")
        }
      })
        .then((response) => {
          const report = response.data;
          this.assessmentDate = moment(report.createdAt).format("DD MMM yyyy");
          this.questions = report.questions;
          // Adjust each question for the SurveySummary component if needed
          for (let question of this.questions) {
            question.title = question.question;
            for (let response of question.responses) {
              response.isSelected = true;
            }
          }
          this.showModal = true;
        })
        .catch((error) => {
          console.error("Error fetching report by reference:", error);
          alert("Report not found for reference: " + this.referenceNumber);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  watch: {
    $route(to) {
      this.loadButton();
      this.updateBeginButton(to.path);
    },
    user(user) {
      this.currentUser = user;
    },
  },
  components: {
    Loading,
    VueFinalModal,
    SurveySummary,
  },
};
</script>

<style lang="scss" scoped>
@import "../breakpoints.scss";

.page-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.content-wrapper {
  margin: 0 auto;
  padding: 20px;
}

.header-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0px;
  text-align: center;

  @include md {
    align-items: center;
    gap: 20px;
  }

  .uni-logo {
    max-width: 500px;
    height: 250px;
    margin-bottom: 20px;
    margin-top: 30px;
    

    @include md {
      margin-bottom: 0;
    }
  }

  .title-container {
    text-align: center;

  }
}

h1 {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 5px;
  line-height: 1.3;
}

h3 {
  font-size: 16px;
  color: #000001;
  font-weight: 500;
  margin-top: 0;
}

.info-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 30px;
  border: 1px solid #E5E7EB;
}

p {
  line-height: 1.6;
  color: #000001;
  margin: 0 0 20px 0;
  font-size: 15px;
}

.btn-primary,
.btn-secondary,
.btn-accent {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 100%;
  margin: 10px 0;
  border: none;

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
    background-color: #0056b3;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #000001;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}

.btn-accent {
  background-color: #013B82;
  color: white;
  font-size: 14px;
}

.btn-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.content-block {
  max-width: 650px;
  margin: 0 auto 40px;
}

.action-buttons {
  display: flex;
  flex-direction: column;
}

/* Search Container Styles */
.search-container {
  max-width: 650px;
  margin: 0 auto 20px;
  margin-top: 20px;
  position: relative;
}

.search-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 20px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);

  &:hover {
    background-color: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
  }

  .search-icon {
    flex-shrink: 0;
  }
}

.reference-search-panel {
  background-color: white;
  border-radius: 0 0 8px 8px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 5px;
  border: 1px solid #E5E7EB;
}

.reference-search {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;

  @include md {
    flex-direction: row;
    align-items: center;
  }

  .input-wrapper {
    position: relative;
    flex-grow: 1;

    .input-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }
  }

  .form-input {
    width: 100%;
    padding: 12px 16px 12px 40px;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: white;

    &:focus {
      border-color: #013B82;
      box-shadow: 0 0 0 3px rgba(1, 59, 130, 0.1);
      outline: none;
    }

    &::placeholder {
      color: #9CA3AF;
    }
  }

  .search-btn {
    margin: 0;
    padding: 12px 20px;
    background: linear-gradient(135deg, #10B981, #059669);
    width: 100%;

    @include md {
      width: auto;
    }
  }
}

/* Animation for the slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-out;
  max-height: 200px;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.external-links-block {
  max-width: 800px;
  margin: 0 auto;
  margin-top: 40px;
  padding: 20px 0;
  border-top: 1px solid #E5E7EB;

  @include md {
    @media (min-height: 800px) {
      position: absolute;
      bottom: 20px;
      left: 50px;
      right: 50px;
      max-width: 800px;
      margin: 0 auto;
    }
  }

  .links-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    @include md {
      justify-content: space-between;
    }
  }

  .ese-logo {
    text-decoration: none;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include md {
      align-items: flex-start;
    }

    img {
      max-height: 40px;
      margin: 5px 0;
    }

    p {
      margin: 0;
      color: #6B7280;
      font-style: italic;
      font-size: 14px;
      font-family: 'Source Sans Pro', sans-serif;
    }
  }

  .buttons-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    max-width: 200px;

    @include md {
      flex-direction: row;
      max-width: none;
    }
  }

  .link-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    background: white;
    color: #000001;
    border: 1px solid #D1D5DB;
    text-decoration: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;

    &:hover {
      background: #F9FAFB;
      color: #111827;
      border-color: #9CA3AF;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    }

    .button-icon {
      flex-shrink: 0;
    }
  }
}

::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 24px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: white;
  width: 90%;
  max-width: 800px;
  overflow-y: auto;
  max-height: 90vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 32px;
  color: #6B7280;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }
}

.modal__content {
  margin-top: 16px;
}


</style>